<script setup lang="ts">
import type { HouiQuery } from "~/types";
import { carCount, priceRange, tsuboRange, houiList } from "~/constants/plan";
const attrs = await usePlanAttr();

// 新築：分類のスタイル
const shintiku = computed(() => {
  return attrs.value
    .filter((attr) => attr.category === 1)
    .map((attr) => ({ id: attr.attr_num, name: attr.cate_name }));
});

// リフォーム：分類のリフォーム
const reform = computed(() => {
  return attrs.value
    .filter((attr) => attr.category === 3)
    .map((attr) => ({ id: attr.attr_num, name: attr.cate_name }));
});

interface Props {
  query: {
    range: number;
    tsubo: number;
    ktype: number;
    kuruma: number;
    houi: HouiQuery;
    attr: string;
  };
}
const props = defineProps<Props>();
const router = useRouter();

const selectedQuery = ref(props.query);

watch(
  () => selectedQuery.value,
  () => {
    router.push({
      query: selectedQuery.value,
    });
  },
  { deep: true },
);

watch(
  () => props.query,
  () => {
    selectedQuery.value = props.query;
  },
  { deep: true },
);
</script>
<template>
  <div>
    <p class="font-bold">絞り込み</p>
    <PlanMenuFilterOpen class="mb-8" />
    <p class="font-bold">並び替え</p>
    <PlanSelectSort class="mb-8" />
    <p class="text-lg font-bold mb-2">工事種別から選ぶ</p>

    <MenuAccordion class="mb-4" title="新築" :open="selectedQuery.attr != ''">
      <ul class="mb-4">
        <li v-for="item in shintiku" :key="item.id">
          <NuxtLink
            class="plan_link mb-4"
            :class="{ isActive: parseInt(selectedQuery.attr) == item.id }"
            :to="{
              path: '/plan',
              query: { ...selectedQuery, attr: item.id, off: 0 },
            }"
          >
            {{ item.name }}
          </NuxtLink>
        </li>
      </ul>
    </MenuAccordion>
    <MenuAccordion
      class="mb-8"
      title="リフォーム"
      :open="selectedQuery.attr != ''"
    >
      <ul class="mb-4">
        <li v-for="item in reform" :key="item.id">
          <NuxtLink
            class="plan_link mb-4"
            :class="{ isActive: parseInt(selectedQuery.attr) == item.id }"
            :to="{
              path: '/plan',
              query: { ...selectedQuery, attr: item.id, off: 0 },
            }"
          >
            {{ item.name }}
          </NuxtLink>
        </li>
      </ul>
    </MenuAccordion>

    <p class="text-lg font-bold mb-2">価格帯から選ぶ</p>
    <MenuAccordion class="mb-8" title="価格帯" :open="selectedQuery.range != 0">
      <ul class="mb-4">
        <li>
          <NuxtLink
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.range == 0 }"
            :to="{
              path: '/plan',
              query: { ...selectedQuery, range: 0, off: 0 },
            }"
          >
            すべて
          </NuxtLink>
        </li>
        <li v-for="item in priceRange" :key="item.id">
          <NuxtLink
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.range == item.id }"
            :to="{
              path: '/plan',
              query: { ...selectedQuery, range: item.id, off: 0 },
            }"
          >
            {{ item.name }}
          </NuxtLink>
        </li>
      </ul>
    </MenuAccordion>

    <p class="text-lg font-bold mb-2">面積から選ぶ</p>
    <MenuAccordion class="mb-8" title="面積" :open="selectedQuery.tsubo != 0">
      <ul class="mb-4">
        <li>
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.tsubo == 0 }"
            @click="
              $router.push({
                path: '/plan',
                query: { ...selectedQuery, tsubo: 0, off: 0 },
              })
            "
          >
            すべて
          </button>
        </li>
        <li v-for="item in tsuboRange" :key="item.id">
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.tsubo == item.id }"
            @click="
              $router.push({
                path: '/plan',
                query: { ...selectedQuery, tsubo: item.id, off: 0 },
              })
            "
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </MenuAccordion>

    <p class="text-lg font-bold mb-2">車台数から選ぶ</p>
    <MenuAccordion
      class="mb-8"
      title="車台数"
      :open="selectedQuery.kuruma != 0"
    >
      <ul class="mb-4">
        <li>
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.kuruma == 0 }"
            @click="
              $router.push({
                path: '/plan',
                query: { ...selectedQuery, kuruma: 0, off: 0 },
              })
            "
          >
            すべて
          </button>
        </li>
        <li v-for="item in carCount" :key="item.id">
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.kuruma == item.id }"
            @click="
              $router.push({
                path: '/plan',
                query: { ...selectedQuery, kuruma: item.id, off: 0 },
              })
            "
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </MenuAccordion>

    <p class="text-lg font-bold mb-2">方位から選ぶ</p>
    <MenuAccordion class="mb-8" title="方位" :open="selectedQuery.houi !== ''">
      <ul class="mb-4">
        <li>
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.houi == '' }"
            @click="
              $router.push({
                path: '/plan',
                query: { ...selectedQuery, houi: '', off: 0 },
              })
            "
          >
            すべて
          </button>
        </li>
        <li v-for="item in houiList" :key="item.name">
          <button
            class="plan_link mb-4"
            :class="{ isActive: selectedQuery.houi == item.name }"
            @click="
              $router.push({
                path: '/plan',
                query: {
                  ...selectedQuery,
                  houi: item.name,
                  off: 0,
                },
              })
            "
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </MenuAccordion>
  </div>
</template>
<style scoped lang="scss">
.plan_link {
  @apply flex items-center border rounded pl-8 py-2 relative  w-full;
  &::before {
    content: "";
    @apply absolute border left-2 rounded-full p-2 bg-white;
  }
}
.isActive {
  @apply border-blue-400 bg-blue-100;
  &::before {
    @apply border-blue-500;
  }
  &::after {
    content: "";
    @apply absolute border border-transparent left-3 rounded-full p-1 bg-blue-500;
  }
}
</style>
